import { Singleton } from '@visiba/instantiation';
import { UnitsApiService } from '@api/units_api.service';
import { I18nAndL10n } from '@modules/i18n_and_l10n/mod';
import { ThemeService } from './theme.service/mod';
import { ThemeModel } from './theme.service/theme_model';
import { LicenceHolderApiService } from '@api/licence_holder_api.service';
import { LicenceHolderService } from './licence_holder.service';
import { capitalizeFirstLetter } from '../../modules/capitalize_first_letter';
import { UserSettingsService } from './user_settings.service';
import type { AppConfigInfo, Licenceholder, Locale, UnitConfigApiModel } from '@api/generated/models';
import type { LicenceHolderLanguageApiModel } from '@api/models/LicenceHolderLanguageApiModel';

export interface LanguageModel {
	code: I18nAndL10n.AvailableVisibaLocales;
	visibaCode: string;
	isSupported: boolean;
	nativeName: string;
	translatedName: string;
}

export interface Config {
	// TODO DEV-11031: Move to public base
	hasMultipleLandingPages: boolean;
	privacyPolicyUrl: string | null;
	termsOfServiceUrl: string | null;
	unitIdOrigin: number;
	receptionId: number | null;
	languages: LanguageModel[];
	theme: { [alias: string]: string | number | boolean };
	translations: I18nAndL10n.TranslationDictionary;
	appConfig: AppConfig | null;
	locale: Locale;
	licenceHolderMetaData: LicenceHolderMetaData;
}

export interface AppConfig {
	id: number;
	displayName: string;
	androidPackageId: string | null;
	appStoreId: string | null;
	storeCompanyName: string | null;
	iosDownloadLink: string | null;
	androidDownloadLink: string | null;
	iosImageUrl: string;
	androidImageUrl: string;
}

export interface LicenceHolderMetaData {
	id: number;
	rootUnit: number;
	displayName: string;
	legalName: string;
	requireClientEmail: boolean;
	requireClientPhone: boolean;
	hasMultipleLandingPages: boolean;
	globalCustomerId: string;
	daysToDepersonalizeClientAfterRequest: number;
}

export class ConfigNotSet extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'ConfigNotSet';
		Object.setPrototypeOf(this, ConfigNotSet.prototype);
	}
}

@Singleton()
export class ConfigService {
	private readonly loadedConfigs = new Map<number, Config>();
	private languagesCache: LanguageModel[] | null = null;

	public readonly __TEMP__loadedConfigsRaw = new Map<number, UnitConfigApiModel>();

	constructor(
		private readonly unitsApiService: UnitsApiService,
		private readonly themeService: ThemeService,
		private readonly licenceHolderService: LicenceHolderService,
		private readonly licenceHolderApiService: LicenceHolderApiService,
		private readonly userSettingsService: UserSettingsService,
	) {
		// Empty
	}

	/** Retrieves the Config for given UnitId */
	public getConfig(unitId: number): Config {
		const config = this.loadedConfigs.get(unitId);
		if (config == null) throw new ConfigNotSet(`No config has been set for ${unitId}`);

		return config;
	}

	/** Loads a config for a given UnitId */
	public async loadConfig(unitId: number): Promise<Config> {
		// TODO: DEV-10573 Check if we can get backend to just add this data to the response
		// instead...
		const languages = await this.getLanguageData();

		let config = this.loadedConfigs.get(unitId);
		if (config != null) {
			return this.getConfig(unitId);
		}

		const { data: unitConfig } = await this.unitsApiService.getUnitConfig(unitId);

		const appConfig = this.parseAppConfig(unitConfig.AppConfig);
		const licenceHolderMetaData = this.parseLicenceHolder(unitConfig.LicenceHolder);

		config = {
			hasMultipleLandingPages: unitConfig.LicenceHolder.HasMultipleLandingPages,
			termsOfServiceUrl: unitConfig.TermsOfServiceUrl,
			privacyPolicyUrl: unitConfig.PrivacyPolicyUrl,
			unitIdOrigin: unitConfig.Unit.UnitId,
			receptionId: unitConfig.ReceptionId,
			languages: languages,
			theme: unitConfig.Theme,
			translations: unitConfig.Translations,
			appConfig: appConfig,
			locale: unitConfig.Locale,
			licenceHolderMetaData: licenceHolderMetaData,
		};

		this.loadedConfigs.set(unitId, config);

		this.__TEMP__loadedConfigsRaw.set(unitId, unitConfig);

		this.themeService.replaceTheme(ThemeModel.createWithOldThemeVariables(config.theme));

		return this.getConfig(unitId);
	}

	private async getLanguageData(): Promise<LanguageModel[]> {
		if (this.languagesCache == null) {
			const { data } = await this.licenceHolderApiService.getSupportedLanguages(this.licenceHolderService.id);

			this.languagesCache = this.mapLanguages(data);
		}

		return Promise.resolve(this.languagesCache);
	}

	private mapLanguages(languages: LicenceHolderLanguageApiModel[]): LanguageModel[] {
		const mappedLanguages: LanguageModel[] = [];
		let isSupported: boolean;

		for (const language of languages) {
			let code: I18nAndL10n.AvailableVisibaLocales;

			try {
				isSupported = true;
				code = I18nAndL10n.convertExternalLocaleToAvailableLocale(language.LanguageCode);
			} catch {
				isSupported = false;
				code = language.LanguageCode as I18nAndL10n.AvailableVisibaLocales;
			}

			mappedLanguages.push({
				code,
				isSupported,
				visibaCode: language.LanguageCode,
				nativeName: capitalizeFirstLetter(language.NativeName),
				translatedName: capitalizeFirstLetter(language.NativeName),
			});
		}

		return mappedLanguages;
	}

	private parseAppConfig(appConfig: AppConfigInfo | null): AppConfig | null {
		if (appConfig == null) return null;
		const config = this.userSettingsService.getSettings();

		let imageLanguage: string;
		switch (config.selectedLanguage) {
			case 'sv': {
				imageLanguage = config.selectedLanguage;
				break;
			}
			default: {
				imageLanguage = 'en';
			}
		}

		return {
			id: appConfig.Id,
			androidPackageId: appConfig.AndroidPackageId,
			appStoreId: appConfig.AppStoreId,
			displayName: appConfig.DisplayName,
			storeCompanyName: appConfig.StoreCompanyName,
			androidDownloadLink:
				appConfig.AndroidPackageId != null
					? `https://play.app.goo.gl/?link=https://play.google.com/store/apps/details?id=${appConfig.AndroidPackageId}`
					: null,
			iosDownloadLink: appConfig.AppStoreId != null ? `https://itunes.apple.com/se/app/id/${appConfig.AppStoreId}?mt=8` : null,
			androidImageUrl: `/new-${imageLanguage}-google-play-badge.png`,
			iosImageUrl: `/new-${imageLanguage}-app-store-badge.svg`,
		};
	}

	private parseLicenceHolder(appConfig: Licenceholder): LicenceHolderMetaData {
		return {
			id: appConfig.Id,
			rootUnit: appConfig.RootUnit,
			legalName: appConfig.LegalName,
			requireClientEmail: appConfig.RequireClientEmail,
			requireClientPhone: appConfig.RequireClientPhone,
			daysToDepersonalizeClientAfterRequest: appConfig.DaysToDepersonalizeClientAfterRequest,
			displayName: appConfig.DisplayName,
			globalCustomerId: appConfig.GlobalCustomerId,
			hasMultipleLandingPages: appConfig.HasMultipleLandingPages,
		};
	}
}
